import React, { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import { getYouTubeVideoId, buildYouTubeEmbedSrc, YOUTUBE_IFRAME_ALLOW } from "../../utils/youtube";

/**
 * Floating, draggable ad-preview popup (bottom-left by default). Renders a YouTube
 * embed for YouTube URLs, a native <video> otherwise. Mount it only while open —
 * position/drag state lives inside and resets on unmount. Esc closes.
 */
export default function VideoPreviewPopup({ url, onClose }) {
  const [pos, setPos] = useState(() => ({ x: 24, y: window.innerHeight - 500 - 24 }));
  const [drag, setDrag] = useState(null); // { startX, startY, originX, originY }

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    if (!drag) return;
    const popupW = 280;
    const popupH = 500;

    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      const nextX = clamp(drag.originX + dx, 8, Math.max(8, window.innerWidth - popupW - 8));
      const nextY = clamp(drag.originY + dy, 8, Math.max(8, window.innerHeight - popupH - 8));
      setPos({ x: nextX, y: nextY });
    };

    const onUp = () => setDrag(null);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const trimmed = (url || "").trim();
  const yt = getYouTubeVideoId(trimmed);

  return (
    <div
      className="fixed z-[9999] rounded-xl overflow-hidden bg-black border border-gray-200 shadow-2xl"
      style={{
        width: 400,
        height: 580,
        left: pos.x,
        top: pos.y,
      }}
    >
      <div
        className={cn(
          "absolute top-0 left-0 right-0 z-10 h-10 bg-black/60 flex items-center justify-between px-3 select-none",
          drag ? "cursor-grabbing" : "cursor-grab"
        )}
        onMouseDown={(e) => {
          // Only start drag with left click
          if (e.button !== 0) return;
          e.preventDefault();
          const rect = e.currentTarget.parentElement?.getBoundingClientRect();
          const originX = rect?.left ?? pos.x ?? 24;
          const originY = rect?.top ?? pos.y ?? 24;
          setDrag({ startX: e.clientX, startY: e.clientY, originX, originY });
        }}
        title="Drag to move"
      >
        <span className="text-xs font-medium text-white/90">Ad preview</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center text-lg hover:bg-black/70"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      {yt ? (
        <iframe
          title="Ad reel"
          src={buildYouTubeEmbedSrc(yt, { autoplay: true })}
          className="w-full h-full border-0 pt-10"
          allow={YOUTUBE_IFRAME_ALLOW}
          allowFullScreen
        />
      ) : trimmed ? (
        <video
          src={trimmed}
          controls
          autoPlay
          className="w-full h-full pt-10 object-contain"
        />
      ) : (
        <div className="w-full h-full pt-10 flex items-center justify-center bg-black">
          <span className="px-3 py-2 rounded-md text-sm font-medium bg-gray-700 text-gray-200">No asset URL</span>
        </div>
      )}
    </div>
  );
}
